import {
  UploadFailedFilesModal,
  UploadProgressModal
} from '@business/workflows/ComponentUI/Dashboard';
import { FilesUploadModal } from '@business/workflows/ComponentUI/UploadFilesModal';
import { getFolderDetails } from '@ports/common/folderStateSlice';
import {
  getAdditionalPhotoMeta,
  getIsFromFavoritePage,
  getIsMediaUploadModalOpen,
  getSubscriberId,
  getToken,
  getUserNotificationData,
  setAdditionalPhotoMeta,
  setIsFromFavoritePage,
  setIsMediaUploadModalOpen,
  setUserNotificationData
} from '@ports/common/sessionStateSlice';
import { getUserData } from '@ports/common/userStateSlice';
import { getUploadFolder } from '@utils/helpers/lib/mediaType';
import {
  uploadMultiplePhotos,
  validateMultipleFiles
} from '@utils/helpers/s3Upload';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import socketService from '@business/services/SocketService';
import { UploadProgressToast } from './UploadProgressToast';

const GROUP_TOAST_ID = 'photo-upload-progress';

export const MediaUploadPersister = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector(getToken);
  const subscriberId = useSelector(getSubscriberId);
  const userDetails = useSelector(getUserData);
  const isOpenFilesModal = useSelector(getIsMediaUploadModalOpen);
  const userNotificationData = useSelector(getUserNotificationData);
  const folderDetails = useSelector(getFolderDetails);
  const isFromFavoritePage = useSelector(getIsFromFavoritePage);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [failedFiles, setFailedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0
  });
  const uploadAbortRef = useRef<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const additionalMeta = useSelector(getAdditionalPhotoMeta);

  useEffect(() => {
    dispatch(
      setUserNotificationData({
        page: '',
        notificationData: null
      })
    );
  }, []);

  useEffect(() => {
    if (
      userNotificationData?.page === 'photos' ||
      userNotificationData?.page === 'recipe'
    ) {
      const current = Number(
        userNotificationData?.notificationData?.uploadIndex
      );
      const total = Number(userNotificationData?.notificationData?.uploadTotal);
      const iconUrl = userNotificationData?.notificationData?.thumbnailUrl;

      if (total === 0) return;

      setUploadProgress({
        current,
        total
      });

      if (current >= total) {
        dispatch(
          setUserNotificationData({
            page: '',
            notificationData: null
          })
        );
      }

      if (!toast.isActive(GROUP_TOAST_ID)) {
        toast.success(
          <UploadProgressToast
            current={current}
            total={total}
            iconUrl={iconUrl}
          />,
          {
            toastId: GROUP_TOAST_ID,
            closeOnClick: true,
            hideProgressBar: true,
            icon: false,
            style: {
              padding: 0, // remove Toastify default padding
              background: 'transparent'
            }
          }
        );
      } else {
        toast.update(GROUP_TOAST_ID, {
          render: (
            <UploadProgressToast
              current={current}
              total={total}
              iconUrl={iconUrl}
            />
          ),
          type: 'success'
        });
      }
    }
  }, [userNotificationData]);

  const photoUploadHandler = async (files: File[]) => {
    if (showUploadModal && files.length === 1) {
      setShowUploadModal(false);
    }

    const validFiles: File[] = [];
    const invalidFiles: File[] = [];

    files.forEach(file => {
      const validation = validateMultipleFiles([file], 1);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      const failedFileNames = invalidFiles.map(f => f.name).join(' and ');
      const errorMsg = t('dashboard.unsupportedFormatMessage', {
        fileNames: failedFileNames
      });

      setUploadError(errorMsg);
      setFailedFiles(invalidFiles);

      toast.error(
        <div>
          <div className="font-medium mb-1">
            {t('dashboard.unsupportedFormat')}
          </div>
          <div className="text-xs">
            {t('dashboard.unsupportedFormatMessage', {
              fileNames: failedFileNames
            })}
          </div>
        </div>,
        {
          autoClose: false,
          onClick: () => setShowUploadModal(true)
        }
      );
      return;
    }

    if (validFiles.length === 0) return;

    const totalFiles = validFiles.length + invalidFiles.length;
    setUploadProgress({ current: 0, total: totalFiles });
    setIsUploading(true);

    const uploadFailedFiles: File[] = [];
    let successCount = 0;

    try {
      const idToken = token || '';

      if (!idToken) {
        throw new Error(t('dashboard.authenticationRequired'));
      }
      socketService.connect();

      for (let i = 0; i < validFiles.length; i++) {
        if (uploadAbortRef.current) break;
        let folder = getUploadFolder(validFiles[i]);
        try {
          const uploadMetadata: any = {
            userId: userDetails?.userId || '',
            folderId: folderDetails?.folderId || '',
            uploadIndex: (i + 1).toString(),
            uploadTotal: totalFiles.toString(),
            ...additionalMeta
          };
          // Only add isFavorite if isFromFavoritePage is true
          if (isFromFavoritePage) {
            uploadMetadata.isFavorite = true;
          }

          // Use contributor's subscriberId for S3 path when uploading on their behalf
          const uploadSubscriberId =
            (additionalMeta as any)?.subscriberId || subscriberId;

          await uploadMultiplePhotos(
            [validFiles[i]],
            idToken,
            uploadSubscriberId,
            folder,
            () => {
              setUploadProgress({
                current: successCount + 1,
                total: totalFiles
              });
            },
            uploadSubscriberId,
            uploadMetadata
          );

          successCount++;
        } catch (fileError: any) {
          console.error(`Failed to upload ${validFiles[i].name}:`, fileError);
          uploadFailedFiles.push(validFiles[i]);
        }
      }

      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });

      // Reset isFromFavoritePage after successful uploads
      if (isFromFavoritePage) {
        dispatch(setIsFromFavoritePage(false));
      }

      // Reset additionalPhotoMeta after upload (e.g. status: 'pending' from sharing module)
      if (Object.keys(additionalMeta || {}).length > 0) {
        dispatch(setAdditionalPhotoMeta({}));
      }

      if (invalidFiles.length > 0) {
        const failedFileNames = invalidFiles.map(f => f.name).join(' and ');
        const errorMsg = t('dashboard.unsupportedFormatMessage', {
          fileNames: failedFileNames
        });

        setUploadError(errorMsg);
        setFailedFiles(invalidFiles);

        toast.error(
          <div>
            <div className="font-semibold mb-1">
              {t('dashboard.uploadProgressPhotos', {
                current: successCount,
                total: totalFiles
              })}
            </div>
            <div className="font-medium mb-1">
              {t('dashboard.unsupportedFormat')}
            </div>
            <div className="text-xs">
              {t('dashboard.unsupportedFormatMessage', {
                fileNames: failedFileNames
              })}
            </div>
          </div>,
          {
            autoClose: false,
            onClick: () => setShowUploadModal(true)
          }
        );
      } else if (uploadFailedFiles.length > 0) {
        const failedFileNames = uploadFailedFiles
          .map(f => f.name)
          .join(' and ');
        const errorMsg = t('dashboard.uploadFailedMessage', {
          fileNames: failedFileNames
        });

        setUploadError(errorMsg);
        setFailedFiles(uploadFailedFiles);

        toast.error(
          <div>
            <div className="font-semibold mb-1">
              {t('dashboard.uploadProgressPhotos', {
                current: successCount,
                total: totalFiles
              })}
            </div>
            <div className="font-medium mb-1">
              {t('dashboard.uploadFailed')}
            </div>
            <div className="text-xs">
              {t('dashboard.uploadFailedMessage', {
                fileNames: failedFileNames
              })}
            </div>
          </div>,
          {
            autoClose: false,
            onClick: () => setShowUploadModal(true)
          }
        );
      }
    } catch (error: any) {
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });

      // Reset isFromFavoritePage even on error
      if (isFromFavoritePage) {
        dispatch(setIsFromFavoritePage(false));
      }

      // Reset additionalPhotoMeta even on error
      if (Object.keys(additionalMeta || {}).length > 0) {
        dispatch(setAdditionalPhotoMeta({}));
      }

      const totalFiles = validFiles.length + invalidFiles.length;
      const failedFileNames = validFiles
        .slice(successCount)
        .map(f => f.name)
        .join(' and ');
      const errorMsg = error.message || t('dashboard.failedToUpload');

      setUploadError(errorMsg);
      setFailedFiles([...validFiles.filter((_, i) => i >= successCount)]);

      toast.error(
        <div>
          <div className="font-semibold mb-1">
            {t('dashboard.uploadProgressPhotos', {
              current: successCount,
              total: totalFiles
            })}
          </div>
          <div className="font-medium mb-1">{t('dashboard.uploadFailed')}</div>
          <div className="text-xs">
            {failedFileNames
              ? t('dashboard.uploadFailedMessage', {
                  fileNames: failedFileNames
                })
              : ''}
            {error.message || t('dashboard.failedToUpload')}
          </div>
        </div>,
        {
          autoClose: false,
          onClick: () => setShowUploadModal(true)
        }
      );
    } finally {
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
      uploadAbortRef.current = false;
    }
  };

  const onSocketMessageReceived = useCallback((data: any) => {
    if (document.visibilityState === 'visible') {
      const payload = data?.payload;
      dispatch(
        setUserNotificationData({
          page: payload?.mediaSource ?? 'photos',
          notificationData: payload
        })
      );
    }
  }, []);

  useEffect(() => {
    socketService.subscribe('onMessage', 'app.event', onSocketMessageReceived);
    return () => {
      socketService.unsubscribe('onMessage', 'app.event');
    };
  }, [onSocketMessageReceived]);

  return (
    <>
      <FilesUploadModal
        isOpen={isOpenFilesModal}
        onClose={() => dispatch(setIsMediaUploadModalOpen(false))}
        onUpload={files => {
          dispatch(setIsMediaUploadModalOpen(false));
          photoUploadHandler(files);
        }}
      />

      <input
        title="Upload photos and videos"
        type="file"
        accept="image/*,video/*"
        multiple
        ref={fileInputRef}
        id="common-photo-upload-input"
        className="hidden"
        onChange={e => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0) {
            photoUploadHandler(files);
          }
        }}
      />

      {showUploadModal && (
        <UploadFailedFilesModal
          isOpen={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            setUploadError('');
            setFailedFiles([]);
          }}
          onUpload={photoUploadHandler}
          errorMessage={uploadError}
          failedFiles={failedFiles}
          setShowUploadModal={setShowUploadModal}
        />
      )}

      {isUploading && (
        <UploadProgressModal
          isOpen={isUploading}
          current={uploadProgress.current}
          total={uploadProgress.total}
          onClose={() => setIsUploading(false)}
        />
      )}
    </>
  );
};
